import type Highcharts from "highcharts";
import type { ActiveAnalysis } from "../../../../api/useActiveAnalysesStream";
import type { Setup } from "../../../../api/useSetups";
import computeInternalGap from "../../../../domain/computeInternalGap";
import t from "../../../../i18n";
import seriesColors from "./seriesColors";
import type { Translation } from "../../../../api/useTranslations";
import type { MetrologyOverrides } from "../../../../api/useMetrologyOverrides";
import { getAnalysisSetup } from "diagnostics-domain/setup/getSetup";
import type { Quantity } from "../../../../api/useQuantities";
import { isAnalysisQuantityValid } from "../../metrology";
export const INTERNAL_GAP_ID = "InternalGap";

function internalGapData({
  analyses,
  setups,
  quantity,
  metrologyOverrides: _metrologyOverrides,
}: {
  analyses: ActiveAnalysis[];
  setups: Setup[];
  quantity: Quantity;
  metrologyOverrides: MetrologyOverrides;
}) {
  const quantitySimulationOutputBySetupId: {
    [setupId: string]: Setup["simulationOutputs"][number];
  } = {};
  for (const setup of setups) {
    const output = setup.simulationOutputs.find(s => s.quantityId === quantity.id);
    if (output) quantitySimulationOutputBySetupId[setup.id] = output;
  }
  const internalGapPoints = analyses.reduce<Highcharts.PointOptionsObject[]>(
    (acc, analysis, index) => {
      const previousAnalysis = analyses[index - 1];

      if (previousAnalysis !== undefined && previousAnalysis.group !== analysis.group) {
        acc.push({
          x: (previousAnalysis.date + analysis.date) / 2,
          high: undefined,
          low: undefined,
        });
      }

      const setup = getAnalysisSetup(setups, analysis);
      if (!setup) {
        acc.push({
          x: analysis.date,
          high: undefined,
          low: undefined,
        });
        return acc;
      }

      const simulationOutput = quantitySimulationOutputBySetupId[setup.id];
      const offset = simulationOutput?.offsetExpected ?? 0;

      const quantityValue = analysis.quantityValues[quantity.id];
      if (!quantityValue) {
        acc.push({
          x: analysis.date,
          high: undefined,
          low: undefined,
        });

        return acc;
      }

      const valid = isAnalysisQuantityValid(analysis, quantity);

      const gap = valid ? computeInternalGap(analysis, quantity.id, offset) : null;

      acc.push({
        x: analysis.date,
        high: gap ?? undefined,
        low: valid ? 0 : undefined,
        custom: {
          unit: quantity.unit,
        },
      });

      return acc;
    },
    [],
  );

  return internalGapPoints;
}

function getInternalGapSerie({
  analyses,
  setups,
  quantity,
  yAxisId,
  legendIndex,
  route,
  metrologyOverrides,
  translations,
}: {
  analyses: ActiveAnalysis[];
  setups: Setup[];
  quantity: Quantity;
  yAxisId: string;
  legendIndex: number;
  route: "diagnosis" | "summary";
  metrologyOverrides: MetrologyOverrides;
  translations: Translation;
}): Highcharts.SeriesArearangeOptions {
  const data: Highcharts.PointOptionsObject[] = internalGapData({
    analyses,
    quantity,
    setups,
    metrologyOverrides,
  });

  return {
    id: INTERNAL_GAP_ID,
    yAxis: yAxisId,
    name:
      route === "summary"
        ? t("charts.legend.internal_losses_global", translations)
        : t("charts.legend.internal_losses", translations),
    color: seriesColors.symptom,
    fillColor: {
      linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
      stops: [
        [0, seriesColors.symptom], // start
        [1, seriesColors.fadedSymptom], // end
      ],
    },
    type: "arearange" as const,
    replicateInNavigator: true,
    data,
    showInLegend: true,
    legendIndex,
  };
}

export { internalGapData };
export default getInternalGapSerie;
